// ═══════════════════════════════════════════════════════════════════════════
//  JELAJAH REMOTE — Spotify · YouTube · Top ElGroup · Cari  (5 Okt 2026)
//
//  Zaldy: tab "Top Spotify / Top YouTube" lama dibuang (lagunya "not in
//  catalogue" dan tidak bisa diklik). Sekarang:
//
//    [ Spotify | YouTube | Top ElGroup | Cari ]      ← baris sumber
//    (🇮🇩 Indonesia) (🇺🇸 Inggris) (🇨🇳 China) (🇯🇵 Jepang) (🇰🇷 Korea)  🔍
//                                                     ← chip bahasa (Spotify & YouTube)
//    (Top) (Pop) (Dangdut) (Rock) (Jazz) …            ← chip genre per bahasa
//
//  Genre (5 Okt 2026) datang dari server (tabel kr_luar_genre, GET …/luar/
//  genre) — menambah genre tidak butuh rilis frontend. "Top" = daftar per
//  bahasa yang lama. Pilihan genre diingat per bahasa selama sesi peramban.
//
//  SATU KETUKAN, tidak ada tombol kedua:
//    · lagu Spotify  → ada di katalog? antre versi karaoke kita;
//                      kalau tidak, server mencari versi karaoke di YouTube
//                      (disinggahkan per trek) lalu langsung mengantrekannya.
//    · lagu YouTube  → langsung antre (IFrame di TV).
//    · Top ElGroup / Cari → lagu katalog kita.
//  Antrean kosong → lagu langsung diputar (server, antrean_otomatis.go).
// ═══════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Search, X, Loader2, Plus, AudioLines, MonitorPlay, Crown, Mic2, Check } from 'lucide-react'
import {
  ambilTopBahasa, ambilTop, ambilGenre, ambilGenreKami, cariLagu, klienLuarTamu, pesanGalat, jam,
  type BahasaTop, type HasilTop, type LaguLuar, type LaguTop, type LaguSiap, type HasilCariYouTube,
  type GenreLuar, type GenreKami,
} from '../lib/tamu'
import { useBahasa, type Kunci } from '../lib/bahasa'
import { tutupKeyboard } from '../lib/keyboard'
import { Sampul, WARNA_KARTU, KerangkaBaris } from './RemoteUi'

type Sumber = 'spotify' | 'youtube' | 'kami'

export type AksiJelajah = {
  /** Lagu katalog kita (versi karaoke lengkap dengan lirik/skor). */
  lokal: (songId: string, judul: string) => Promise<void>
  /** Video YouTube → antrean. */
  youtube: (l: LaguLuar) => Promise<void>
  /** Lagu tanpa video/katalog → cari versi karaoke di YouTube → antrean. */
  karaoke: (l: LaguLuar) => Promise<void>
}

// Nama "Spotify / YouTube / Top ElGroup" adalah merek — tidak diterjemahkan.
// 5 Okt 2026: ikon GENERIK lucide (bukan logo merek, bukan ikon merek
// lucide `Youtube`) — hijau lembut untuk Spotify, merah lembut untuk YouTube.
// Warna hanya saat tab tidak aktif; tab aktif berlatar emas → ikon hitam.
const SUMBER: { id: Sumber; label: string | Kunci; terjemah?: boolean; ikon: ReactNode; warna?: string }[] = [
  { id: 'spotify', label: 'Spotify', ikon: <AudioLines size={20} />, warna: 'text-emerald-300/90' },
  { id: 'youtube', label: 'YouTube', ikon: <MonitorPlay size={20} />, warna: 'text-rose-300/90' },
  { id: 'kami', label: 'Top ElGroup', ikon: <Crown size={20} /> },
]
// 8 Okt 2026 — Zaldy: "cari lagunya atur di masing-masing section aja …
// yang di kanan 'Cari lagu' nggak usah". Tab Cari dibuang; tiap sumber punya
// kotak cari sendiri yang selalu terlihat di atas daftarnya.

const BAHASA: { id: BahasaTop; bendera: string; kunci: Kunci }[] = [
  { id: 'id', bendera: '🇮🇩', kunci: 'kat.id' },
  { id: 'en', bendera: '🇺🇸', kunci: 'kat.en' },
  { id: 'zh', bendera: '🇨🇳', kunci: 'kat.zh' },
  { id: 'ja', bendera: '🇯🇵', kunci: 'kat.ja' },
  { id: 'ko', bendera: '🇰🇷', kunci: 'kat.ko' },
]

// Pilihan terakhir diingat per perangkat — kenyamanan saja, boleh hilang.
const K_SUMBER = 'karaoke.remote.sumber'
const K_BAHASA = 'karaoke.remote.bahasa'
const K_YT_KARAOKE = 'karaoke.remote.ytKaraoke'
const baca = (k: string) => { try { return localStorage.getItem(k) ?? '' } catch { return '' } }
const simpan = (k: string, v: string) => { try { localStorage.setItem(k, v) } catch { /* privat */ } }

// Genre terakhir per bahasa — hanya selama sesi peramban (sessionStorage):
// tamu berikutnya di tablet kamar mulai lagi dari "Top".
const K_GENRE = 'karaoke.remote.genre'
const bacaGenreSesi = (): Partial<Record<BahasaTop, string>> => {
  try { return JSON.parse(sessionStorage.getItem(K_GENRE) || '{}') ?? {} } catch { return {} }
}
const simpanGenreSesi = (v: Partial<Record<BahasaTop, string>>) => {
  try { sessionStorage.setItem(K_GENRE, JSON.stringify(v)) } catch { /* privat */ }
}

// Daftar genre disinggahkan di memori 10 menit (statusnya ikut berubah saat
// pemanas server mengisi genre baru).
const simpananGenre: { kode: string; data: GenreLuar[]; t: number } = { kode: '', data: [], t: 0 }
const genreSegar = (kode: string) => simpananGenre.kode === kode && Date.now() - simpananGenre.t < 600_000

export function JelajahRemote({ kode, aksi, gulirDaftar = false }: {
  kode: string; aksi: AksiJelajah
  /** 6 Okt: ≥ lg — judul, baris sumber & chip diam, hanya daftar lagu yang digulir. */
  gulirDaftar?: boolean
}) {
  const { t, b } = useBahasa()
  const [sumber, setSumberState] = useState<Sumber>(() => {
    const s = baca(K_SUMBER) as Sumber
    return SUMBER.some(x => x.id === s) ? s : 'spotify'
  })
  const [bhs, setBhsState] = useState<BahasaTop>(() => {
    const s = baca(K_BAHASA) as BahasaTop
    return BAHASA.some(x => x.id === s) ? s : (BAHASA.some(x => x.id === b) ? b as BahasaTop : 'id')
  })
  const setSumber = (s: Sumber) => { setSumberState(s); simpan(K_SUMBER, s) }
  const setBhs = (v: BahasaTop) => { setBhsState(v); simpan(K_BAHASA, v) }

  // ── Genre per bahasa ──
  const [daftarGenre, setDaftarGenre] = useState<GenreLuar[] | null>(() => genreSegar(kode) ? simpananGenre.data : null)
  useEffect(() => {
    if (genreSegar(kode)) { setDaftarGenre(simpananGenre.data); return }
    let hidup = true
    ambilGenre(kode)
      .then(d => { Object.assign(simpananGenre, { kode, data: d, t: Date.now() }); if (hidup) setDaftarGenre(d) })
      .catch(() => { if (hidup) setDaftarGenre([]) })
    return () => { hidup = false }
  }, [kode])
  const [genrePer, setGenrePer] = useState(bacaGenreSesi)
  const genreIngat = genrePer[bhs] ?? ''
  // Genre yang diingat tapi sudah tidak ada (dinonaktifkan) → kembali ke Top.
  const genre = genreIngat && daftarGenre && !daftarGenre.some(g => g.bahasa === bhs && g.kode === genreIngat) ? '' : genreIngat
  const setGenre = (g: string) => {
    const baru = { ...genrePer, [bhs]: g }
    setGenrePer(baru); simpanGenreSesi(baru)
  }
  // Daftar genre yang baru terisi → chip-nya tidak redup lagi.
  const tandaiTerisi = (s: 'spotify' | 'youtube', b: BahasaTop, g: string) => {
    if (!g) return
    setDaftarGenre(d => {
      if (!d) return d
      const k = s === 'spotify' ? 'sp' : 'yt'
      if (d.some(x => x.bahasa === b && x.kode === g && x[k].ada)) return d
      const baru = d.map(x => x.bahasa === b && x.kode === g ? { ...x, [k]: { ...x[k], ada: true } } : x)
      if (simpananGenre.kode === kode) simpananGenre.data = baru
      return baru
    })
  }

  // true = sedang mengetik/mencari di sumber aktif → chip bahasa/genre disembunyikan.
  const [cariAktif, setCariAktif] = useState(false)
  // 21 Okt 2026 — Zaldy: "tombol karaoke setiap diklik ngerefresh list,
  // harusnya nggak harus di-search dulu". ✓ Karaoke (tab YouTube) kini
  // berlaku juga untuk daftar Top: ketuk lagu → server mencari & mengantre
  // VERSI KARAOKE-nya (satu pencarian per ketukan, seperti lagu Spotify).
  // Diingat per perangkat.
  const [ytKaraoke, setYtKaraokeState] = useState(() => baca(K_YT_KARAOKE) !== '0')
  const setYtKaraoke = (v: boolean) => { setYtKaraokeState(v); simpan(K_YT_KARAOKE, v ? '1' : '0') }
  const [sibuk, setSibuk] = useState('')
  const jalankan = async (kunci: string, f: () => Promise<void>) => {
    if (sibuk) return
    setSibuk(kunci)
    try { await f() } finally { setSibuk('') }
  }

  const idx = SUMBER.findIndex(s => s.id === sumber)
  const luar = sumber === 'spotify' || sumber === 'youtube'
  const ket: Kunci = sumber === 'spotify' ? 'jelajah.ketSp2' : sumber === 'youtube' ? 'jelajah.ketYt2'
    : 'jelajah.ketKami'

  return (
    <section className={`mt-5 tab:mt-3 ${gulirDaftar ? 'tab:flex tab:flex-col tab:flex-1 tab:min-h-0' : ''}`}>
      {/* 22 Okt (Zaldy: "susah scroll, kecilin aja"): di tablet kepala jelajah
          (judul, sumber, chip) dipadatkan; di layar pendek judul disembunyikan. */}
      <div className="flex items-end justify-between gap-3 mb-3 tab:mb-2 pendek:hidden">
        <h3 className="text-[21px] tab:text-[18px] font-semibold tracking-tight">{t('jelajah.judul')}</h3>
        <span className="text-[12px] text-text-3 text-right leading-tight">{t(ket)}</span>
      </div>

      {/* ── Baris sumber: segmen dengan penanda emas yang bergeser ── */}
      {/* 2 Nov 2026 — Zaldy: baris Spotify / YouTube / Top ElGroup diperbesar. */}
      <div className="relative grid grid-cols-3 p-1.5 rounded-2xl bg-white/[0.045] border border-white/[0.08]">
        <span aria-hidden
          className="absolute top-1.5 bottom-1.5 left-1.5 rounded-xl bg-gradient-to-b from-gold-1 to-gold shadow-[0_6px_18px_-6px_rgba(212,175,55,.7)]
                     transition-transform duration-300 ease-[cubic-bezier(.2,.8,.2,1)] motion-reduce:transition-none"
          style={{ width: 'calc((100% - 12px) / 3)', transform: `translateX(${idx * 100}%)` }} />
        {SUMBER.map(s => (
          <button key={s.id} onClick={() => { setSumber(s.id); setCariAktif(false) }}
            className={`rm-tekan relative z-10 h-14 tab:h-14 pendek:h-12 rounded-xl inline-flex items-center justify-center gap-2 text-[15.5px] sm:text-[17px] font-semibold
              ${sumber === s.id ? 'text-black font-semibold' : 'text-text-2 hover:text-text'}`}>
            <span className={`inline-flex transition-colors ${sumber === s.id ? '' : s.warna ?? ''}`}>{s.ikon}</span>
            <span className="truncate">{s.terjemah ? t(s.label as Kunci) : s.label}</span>
          </button>
        ))}
      </div>

      {/* Kotak cari tiap sumber selalu di atas; daftar top / Top ElGroup
          tampil selama kotaknya kosong (lihat prop `bawaan`). */}
      <div key={sumber} className={`rm-pudar mt-3 tab:mt-2 ${gulirDaftar ? 'tab:flex-1 tab:min-h-0 tab:overflow-y-auto tab:overscroll-contain tab:pr-1 tab:pb-2' : ''}`}>
        {/* 22 Okt (Zaldy: "genre ketutupan, bikin bisa di-scroll aja dari
            negaranya"): chip bahasa & genre ikut DIGULIR bersama daftar —
            yang diam hanya baris sumber (Spotify/YouTube/Top ElGroup). Kotak
            cari tetap menempel di atas saat daftar digulir (LEKAT). */}
        {luar && !cariAktif && <div className="mb-3 tab:mb-2">
          {/* ── Chip bahasa (Spotify & YouTube) — disembunyikan saat sedang mencari ── */}
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0 flex gap-2 overflow-x-auto rm-tanpa-gulir py-0.5">
              {BAHASA.map(x => (
                <button key={x.id} onClick={() => setBhs(x.id)}
                  className={`rm-tekan shrink-0 inline-flex items-center gap-1.5 h-11 tab:h-10 pendek:h-9 px-4 pendek:px-3.5 rounded-full text-[16px] pendek:text-[15px] border
                    ${bhs === x.id
                      ? 'bg-gold/15 border-gold/50 text-gold-1 font-semibold'
                      : 'bg-white/[0.04] border-white/10 text-text-2 hover:bg-white/[0.08]'}`}>
                  <span className="text-[18px] leading-none">{x.bendera}</span> {t(x.kunci)}
                </button>
              ))}
            </div>
          </div>
          {/* ── Chip genre per bahasa (Spotify & YouTube) ── */}
          <BarisGenre daftar={daftarGenre} bhs={bhs} sumber={sumber} pilih={genre} ubah={setGenre} />
        </div>}
        {sumber === 'spotify' && (
          <CariSpotify kode={kode} aksi={aksi} sibuk={sibuk} jalankan={jalankan} onAktif={setCariAktif}
            bawaan={<DaftarTop key={`sp:${bhs}:${genre}`} kode={kode} sumber="spotify" bhs={bhs} genre={genre} onTerisi={tandaiTerisi} aksi={aksi} sibuk={sibuk} jalankan={jalankan} />} />)}
        {sumber === 'youtube' && (
          <CariYouTube kode={kode} aksi={aksi} sibuk={sibuk} jalankan={jalankan} onAktif={setCariAktif}
            karaoke={ytKaraoke} setKaraoke={setYtKaraoke}
            bawaan={<DaftarTop key={`yt:${bhs}:${genre}`} kode={kode} sumber="youtube" bhs={bhs} genre={genre} onTerisi={tandaiTerisi} aksi={aksi} sibuk={sibuk} jalankan={jalankan} versiKaraoke={ytKaraoke} />} />)}
        {sumber === 'kami' && (
          <CariKatalog kode={kode} aksi={aksi} sibuk={sibuk} jalankan={jalankan}
            bawaan={<DaftarKami kode={kode} aksi={aksi} sibuk={sibuk} jalankan={jalankan} />} />)}
      </div>
    </section>
  )
}

type PropDaftar = {
  kode: string; aksi: AksiJelajah; sibuk: string
  jalankan: (kunci: string, f: () => Promise<void>) => Promise<void>
}
// 8 Okt: kotak cari per sumber — `bawaan` = isi saat kotak kosong (daftar top /
// Top ElGroup); `onAktif` memberi tahu induk supaya chip bahasa/genre disembunyikan.
type PropCari = PropDaftar & { bawaan?: ReactNode; onAktif?: (aktif: boolean) => void }

// Satu ketukan: pilih jalur sesuai jenis lagu.
function tekanLuar(l: LaguLuar, aksi: AksiJelajah, versiKaraoke = false): { f: () => Promise<void>; cariKaraoke: boolean } {
  if (l.song_id) return { f: () => aksi.lokal(l.song_id!, l.judul), cariKaraoke: false }
  // ✓ Karaoke di daftar Top YouTube: video chart (MV resmi) → cari versi karaokenya.
  if (l.sumber === 'youtube' && l.id && !versiKaraoke) return { f: () => aksi.youtube(l), cariKaraoke: false }
  return { f: () => aksi.karaoke(l), cariKaraoke: true }
}

function DaftarLuar({ lagu, aksi, sibuk, jalankan, nomor = true, versiKaraoke = false }: Omit<PropDaftar, 'kode'> & { lagu: LaguLuar[]; nomor?: boolean; versiKaraoke?: boolean }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {lagu.map((l, k) => {
        const kunci = `${l.sumber}:${l.id || l.judul}:${k}`
        const { f, cariKaraoke } = tekanLuar(l, aksi, versiKaraoke)
        return (
          <BarisLagu key={kunci} i={k} nomor={nomor ? (l.posisi ?? k + 1) : undefined}
            judul={l.judul} artis={l.artis} gambar={l.sampul} lebar={l.sumber === 'youtube'}
            karaoke={!!l.song_id} durasi={l.durasi}
            sibuk={sibuk === kunci} mencari={sibuk === kunci && cariKaraoke}
            onKlik={() => jalankan(kunci, f)} />
        )
      })}
    </div>
  )
}

// ── TOP PER BAHASA ─────────────────────────────────────────────────────────

// Singgahan di memori tablet — 1 jam (tablet kamar bisa terbuka berhari-hari;
// server sendiri menyegarkan daftarnya tiap 12 jam).
const simpananTop: Record<string, HasilTop & { _t: number }> = {}
const masihSegar = (k: string) => !!simpananTop[k] && Date.now() - simpananTop[k]._t < 3600_000

function DaftarTop({ kode, sumber, bhs, genre, onTerisi, versiKaraoke = false, ...p }: PropDaftar & {
  sumber: 'spotify' | 'youtube'; bhs: BahasaTop; genre: string
  onTerisi?: (s: 'spotify' | 'youtube', b: BahasaTop, g: string) => void
  versiKaraoke?: boolean
}) {
  const { t } = useBahasa()
  const kunci = `${kode}:${sumber}:${bhs}:${genre}`
  const [data, setData] = useState<HasilTop | null>(masihSegar(kunci) ? simpananTop[kunci] : null)
  const [galat, setGalat] = useState('')
  const terisi = useRef(onTerisi)
  terisi.current = onTerisi

  useEffect(() => {
    let hidup = true
    setGalat('')
    if (masihSegar(kunci)) { setData(simpananTop[kunci]); return }
    setData(null)
    ambilTopBahasa(kode, sumber, bhs, genre)
      .then(d => {
        if (!hidup) return
        if (d.lagu.length) { simpananTop[kunci] = { ...d, _t: Date.now() }; terisi.current?.(sumber, bhs, genre) }
        setData(d)
      })
      .catch(e => { if (hidup) { setGalat(pesanGalat(e)); setData({ lagu: [] }) } })
    return () => { hidup = false }
  }, [kode, sumber, bhs, genre, kunci])

  if (!data) return <KerangkaBaris n={8} />
  if (data.lagu.length === 0) {
    // Catatan teknis server (kuota, playlist) tidak ditampilkan ke tamu.
    return <Kosong>{galat || (genre ? t('genre.belum') : t('jelajah.chartKosong'))}</Kosong>
  }
  // 30 Okt (Zaldy: "top2nya ko ga ganti"): kapan daftar ini terakhir ditarik.
  const waktu = data.diperbarui ? new Date(data.diperbarui) : null
  return (
    <>
      {waktu && !isNaN(waktu.getTime()) && (
        <div className="mb-2 text-[11.5px] text-text-3">
          {t('jelajah.diperbarui', { waktu: waktu.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) })}
        </div>
      )}
      <DaftarLuar lagu={data.lagu} versiKaraoke={versiKaraoke} {...p} />
    </>
  )
}

// ── CHIP GENRE ─────────────────────────────────────────────────────────────

function BarisGenre({ daftar, bhs, sumber, pilih, ubah }: {
  daftar: GenreLuar[] | null; bhs: BahasaTop; sumber: 'spotify' | 'youtube'
  pilih: string; ubah: (g: string) => void
}) {
  const { t, b } = useBahasa()
  if (!daftar) {
    return (
      <div className="mt-2.5 tab:mt-1.5 flex gap-2 overflow-hidden py-0.5" aria-hidden>
        {[44, 56, 72, 52, 60].map((w, i) => <span key={i} className="rm-kerangka shrink-0 h-8 rounded-full" style={{ width: w }} />)}
      </div>
    )
  }
  const milik = daftar.filter(g => g.bahasa === bhs)
  if (milik.length === 0) return null
  const k = sumber === 'spotify' ? 'sp' : 'yt'
  const chip = (kode: string, label: string, ada: boolean, i: number) => (
    <button key={kode || 'top'} type="button" aria-pressed={pilih === kode}
      style={{ ['--i' as string]: i }}
      onClick={e => { ubah(kode); e.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' }) }}
      className={`rm-tekan rm-muncul shrink-0 h-10 pendek:h-9 px-4 pendek:px-3.5 rounded-full text-[15px] border
        ${pilih === kode
          ? 'bg-white text-black border-white font-semibold shadow-[0_4px_14px_-6px_rgba(255,255,255,.55)]'
          : ada
            ? 'bg-transparent border-white/15 text-text-2 hover:bg-white/[0.06]'
            : 'bg-transparent border-dashed border-white/15 text-text-3 hover:bg-white/[0.04]'}`}>
      {label}
    </button>
  )
  return (
    <div key={bhs} role="group" aria-label={t('genre.pilih')}
      className="rm-pudar mt-2.5 tab:mt-1.5 flex gap-2 overflow-x-auto rm-tanpa-gulir py-0.5 -mx-1 px-1">
      {chip('', t('genre.top'), true, 0)}
      {milik.map((g, i) => chip(g.kode, g.nama_i18n?.[b as BahasaTop] || g.nama, g[k].ada, i + 1))}
    </div>
  )
}

// ── CARI SPOTIFY (jeda 600 ms, disinggahkan server per kueri) ──────────────

function CariSpotify({ kode, bawaan, onAktif, ...p }: PropCari) {
  const { t } = useBahasa()
  const klien = useMemo(() => klienLuarTamu(kode), [kode])
  const [q, setQ] = useState('')
  const [hasil, setHasil] = useState<LaguLuar[] | null>(null)
  const [memuat, setMemuat] = useState(false)
  const [galat, setGalat] = useState('')
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => {
    window.clearTimeout(timer.current)
    if (q.trim().length < 2) { setHasil(null); return }
    timer.current = window.setTimeout(async () => {
      setMemuat(true); setGalat('')
      try { setHasil((await klien.cariSpotify(q.trim())).lagu) } catch (e) { setGalat(pesanGalat(e)) }
      finally { setMemuat(false) }
    }, 600)
    return () => window.clearTimeout(timer.current)
  }, [q, klien])

  const aktif = q.trim().length >= 2
  useEffect(() => { onAktif?.(aktif) }, [aktif, onAktif])

  return (
    <div className="space-y-3">
      <div className={LEKAT}><KotakCari nilai={q} ubah={setQ} placeholder={t('jelajah.cariSpLagu')} memuat={memuat} /></div>
      {galat && <div className="text-[13px] text-red-300">{galat}</div>}
      {!aktif && bawaan}
      {aktif && memuat && !hasil && <KerangkaBaris n={4} />}
      {aktif && hasil && hasil.length === 0 && !memuat && <Kosong>{t('luar.tidakAdaHasil')}</Kosong>}
      {aktif && hasil && hasil.length > 0 && <DaftarLuar lagu={hasil} nomor={false} {...p} />}
    </div>
  )
}

// ── CARI YOUTUBE (hanya saat ditekan — kuota sempit) ───────────────────────

function CariYouTube({ kode, bawaan, onAktif, karaoke, setKaraoke, ...p }: PropCari & {
  karaoke: boolean; setKaraoke: (v: boolean) => void
}) {
  const { t } = useBahasa()
  const klien = useMemo(() => klienLuarTamu(kode), [kode])
  const [q, setQ] = useState('')
  const [hasil, setHasil] = useState<HasilCariYouTube | null>(null)
  const [memuat, setMemuat] = useState(false)
  const [galat, setGalat] = useState('')

  async function cari(modeKaraoke = karaoke) {
    const k = q.trim()
    if (k.length < 2 || memuat) return
    setMemuat(true); setGalat('')
    try { setHasil(await klien.cariYouTube(k, modeKaraoke)) } catch (e) { setGalat(pesanGalat(e)) }
    finally { setMemuat(false) }
  }
  // Ganti ✓ Karaoke saat sudah ada hasil cari → langsung dicari ulang dengan
  // mode baru (dulu harus tekan Cari lagi).
  const ubahKaraoke = () => {
    const baru = !karaoke
    setKaraoke(baru)
    if (hasil !== null && q.trim().length >= 2) void cari(baru)
  }
  // Kotak dikosongkan → hasil dibuang, daftar top kembali.
  useEffect(() => { if (q.trim() === '') setHasil(null) }, [q])
  const aktif = hasil !== null || memuat
  useEffect(() => { onAktif?.(aktif) }, [aktif, onAktif])

  return (
    <div className="space-y-3">
      <form onSubmit={e => { e.preventDefault(); void cari() }} className={`flex items-center gap-2 ${LEKAT}`}>
        <div className="flex-1 min-w-0"><KotakCari nilai={q} ubah={setQ} placeholder={t('luar.cariYoutube')} /></div>
        <button type="button" onClick={ubahKaraoke}
          className={`rm-tekan shrink-0 h-11 tab:h-10 px-3 rounded-2xl border text-[13px] font-medium
            ${karaoke ? 'bg-[#3a3424] border-gold/45 text-gold-1' : 'bg-[#1a1e40] border-white/10 text-text-3'}`}>
          {karaoke ? '✓ ' : ''}{t('luar.karaoke')}
        </button>
        <button type="submit" disabled={memuat}
          className="rm-tekan shrink-0 h-11 tab:h-10 px-4 rounded-2xl bg-gradient-to-b from-red-500 to-red-600 text-white text-[14px] font-semibold disabled:opacity-60">
          {memuat ? <Loader2 size={15} className="animate-spin" /> : t('luar.cari')}
        </button>
      </form>
      <div className="text-[12px] text-text-3 pendek:hidden">{t('luar.kuotaCatatan')}</div>
      {karaoke && !aktif && <div className="text-[12.5px] text-gold-1/90 pendek:hidden">{t('luar.karaokeTop')}</div>}
      {galat && <div className="text-[13px] text-red-300">{galat}</div>}
      {hasil?.perlu_kunci && <div className="text-[13px] text-amber-300">{t('luar.tanpaKunciYt')}</div>}
      {!aktif && bawaan}
      {memuat && !hasil && <KerangkaBaris n={4} />}
      {hasil && !hasil.perlu_kunci && hasil.video.length === 0 && !memuat && <Kosong>{t('luar.tidakAdaHasil')}</Kosong>}
      {hasil && hasil.video.length > 0 && <DaftarLuar lagu={hasil.video} nomor={false} {...p} />}
    </div>
  )
}

// ── TOP ELGROUP (paling sering dinyanyikan di outlet ini) ─────────────────

// 26 Okt (Zaldy: "Top ElGroup tambahin filter negara dan juga genre"):
// chip negara (bahasa katalog) + chip genre yang ADA di katalog untuk negara
// itu. Diingat selama sesi peramban, seperti genre Spotify/YouTube.
const K_KAMI = 'karaoke.remote.kami'
const bacaKami = (): { b: string; g: string } => {
  try { return { b: '', g: '', ...JSON.parse(sessionStorage.getItem(K_KAMI) || '{}') } } catch { return { b: '', g: '' } }
}

function DaftarKami({ kode, aksi, sibuk, jalankan }: PropDaftar) {
  const { t, b } = useBahasa()
  const [pil, setPil] = useState(bacaKami)
  const ubahPil = (v: { b: string; g: string }) => {
    setPil(v)
    try { sessionStorage.setItem(K_KAMI, JSON.stringify(v)) } catch { /* privat */ }
  }
  const [genreAda, setGenreAda] = useState<GenreKami[] | null>(null)
  useEffect(() => {
    let hidup = true
    setGenreAda(null)
    ambilGenreKami(kode, pil.b).then(d => { if (hidup) setGenreAda(d) }).catch(() => { if (hidup) setGenreAda([]) })
    return () => { hidup = false }
  }, [kode, pil.b])
  // Genre terpilih yang tidak ada di negara baru → kembali ke semua genre.
  const genre = pil.g && genreAda && !genreAda.some(x => x.genre.toLowerCase() === pil.g.toLowerCase()) ? '' : pil.g

  const [daftar, setDaftar] = useState<LaguTop[] | null>(null)
  useEffect(() => {
    let hidup = true
    setDaftar(null)
    ambilTop(kode, 'kami', pil.b, genre).then(d => { if (hidup) setDaftar(d) }).catch(() => { if (hidup) setDaftar([]) })
    return () => { hidup = false }
  }, [kode, pil.b, genre])

  const chipNegara = (id: string, label: ReactNode) => (
    <button key={id || 'semua'} onClick={() => ubahPil({ b: id, g: '' })}
      className={`rm-tekan shrink-0 inline-flex items-center gap-1.5 h-11 tab:h-10 pendek:h-9 px-4 pendek:px-3.5 rounded-full text-[16px] pendek:text-[15px] border
        ${pil.b === id
          ? 'bg-gold/15 border-gold/50 text-gold-1 font-semibold'
          : 'bg-white/[0.04] border-white/10 text-text-2 hover:bg-white/[0.08]'}`}>
      {label}
    </button>
  )
  const chipGenre = (g: string, label: string) => (
    <button key={g || 'semua'} type="button" aria-pressed={genre === g}
      onClick={e => { ubahPil({ b: pil.b, g }); e.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' }) }}
      className={`rm-tekan shrink-0 h-10 pendek:h-9 px-4 pendek:px-3.5 rounded-full text-[15px] border
        ${genre === g
          ? 'bg-white text-black border-white font-semibold'
          : 'bg-transparent border-white/15 text-text-2 hover:bg-white/[0.06]'}`}>
      {label}
    </button>
  )
  const saring = (
    <div className="mb-3 tab:mb-2">
      <div className="flex gap-2 overflow-x-auto rm-tanpa-gulir py-0.5">
        {chipNegara('', t('kat.semua'))}
        {BAHASA.map(x => chipNegara(x.id, <><span className="text-[18px] leading-none">{x.bendera}</span> {t(x.kunci)}</>))}
      </div>
      {genreAda && genreAda.length > 0 && (
        <div role="group" aria-label={t('genre.pilih')}
          className="mt-2.5 tab:mt-1.5 flex gap-2 overflow-x-auto rm-tanpa-gulir py-0.5 -mx-1 px-1">
          {chipGenre('', t('genre.top'))}
          {genreAda.map(x => chipGenre(x.genre, x.nama_i18n?.[b as BahasaTop] || x.nama || x.genre))}
        </div>
      )}
    </div>
  )

  if (!daftar) return <>{saring}<KerangkaBaris n={8} /></>
  const bisa = daftar.filter(l => l.song_id)
  if (bisa.length === 0) return <>{saring}<Kosong>{t('jelajah.kamiKosong')}</Kosong></>
  return (
    <>{saring}
    <div className="grid gap-2 sm:grid-cols-2">
      {bisa.map((l, k) => {
        const kunci = `kami:${l.song_id}`
        return (
          <BarisLagu key={kunci} i={k} nomor={l.posisi} judul={l.judul} artis={l.artis}
            karaoke catatan={l.jml_antre ? `${l.jml_antre}×` : undefined}
            sibuk={sibuk === kunci} onKlik={() => jalankan(kunci, () => aksi.lokal(l.song_id!, l.judul))} />
        )
      })}
    </div>
    </>
  )
}

// ── CARI KATALOG KITA ──────────────────────────────────────────────────────

function CariKatalog({ kode, aksi, sibuk, jalankan, bawaan }: PropCari) {
  const { t } = useBahasa()
  const [q, setQ] = useState('')
  const [hasil, setHasil] = useState<LaguSiap[] | null>(null)
  const [memuat, setMemuat] = useState(false)
  const timer = useRef<number | undefined>(undefined)

  const aktif = q.trim() !== ''
  useEffect(() => {
    window.clearTimeout(timer.current)
    if (!aktif) { setHasil(null); return }
    timer.current = window.setTimeout(async () => {
      setMemuat(true)
      try { setHasil(await cariLagu(kode, q)) } catch { /* biarkan */ }
      finally { setMemuat(false) }
    }, 350)
    return () => window.clearTimeout(timer.current)
  }, [q, kode, aktif])

  const catatan = (l: LaguSiap) =>
    [l.ada_lirik ? t('jelajah.lirik') : '', l.ada_melodi ? t('jelajah.skorNada') : ''].filter(Boolean).join(' · ')

  return (
    <div className="space-y-3">
      <div className={LEKAT}><KotakCari nilai={q} ubah={setQ} placeholder={t('jelajah.ketCari')} memuat={memuat} /></div>
      {!aktif && bawaan}
      {aktif && !hasil && <KerangkaBaris n={4} />}
      {aktif && hasil && hasil.length === 0 && !memuat && <Kosong>{t('jelajah.tidakCocok')}</Kosong>}
      {hasil && hasil.length > 0 && (
        <div className="grid gap-2 sm:grid-cols-2">
          {hasil.map((l, k) => {
            const kunci = `cari:${l.id}`
            return (
              <BarisLagu key={kunci} i={k} judul={l.judul} artis={l.artis} karaoke catatan={catatan(l) || undefined}
                durasi={l.durasi_detik ?? undefined}
                sibuk={sibuk === kunci} onKlik={() => jalankan(kunci, () => aksi.lokal(l.id, l.judul))} />
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── POTONGAN ───────────────────────────────────────────────────────────────

// 22 Okt (Zaldy): kotak cari ikut DIAM di atas daftar (≥ tab) — yang digulir
// hanya daftar lagunya. Latar gelap supaya baris yang lewat di bawahnya tidak
// tembus.
// 26 Okt (Zaldy: "scroll-nya patah-patah"): backdrop-blur di elemen yang
// melayang di atas daftar = blur ulang tiap frame gulir — berat di tablet murah.
// Latar padat saja.
// 30 Okt (Zaldy, screenshot: "ini jelek dah"): latar padat #0b0d1f tampil
// sebagai KOTAK GELAP bersudut tajam di atas latar gradien halaman. Sekarang
// pembungkusnya transparan; yang padat hanya kotak cari itu sendiri (lihat
// KotakCari), jadi baris yang lewat di bawahnya tetap tertutup tanpa kotak.
const LEKAT = 'tab:sticky tab:top-0 tab:z-10 tab:pb-1.5'


function KotakCari({ nilai, ubah, placeholder, memuat, fokus }: {
  nilai: string; ubah: (v: string) => void; placeholder: string; memuat?: boolean; fokus?: boolean
}) {
  return (
    <div className="relative rm-muncul">
      <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-3" />
      {/* 26 Okt (Zaldy): tombol ✓/Cari di keyboard tablet harus menutup keyboard
          — pencarian sudah jalan sambil mengetik, jadi Enter cukup melepas fokus. */}
      <input value={nilai} onChange={e => ubah(e.target.value)} placeholder={placeholder} autoFocus={fokus}
        enterKeyHint="search"
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); tutupKeyboard(e.currentTarget) } }}
        className="w-full !h-11 tab:!h-10 !bg-[#1a1e40] !border-white/10 !rounded-2xl !pl-10 !pr-10 !text-[14.5px] focus:!border-gold/50
                   shadow-[0_8px_20px_-12px_rgba(0,0,0,.8)]" />
      {memuat
        ? <Loader2 size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 animate-spin text-text-3" />
        : nilai && (
          <button type="button" onClick={() => ubah('')} aria-label="×"
            className="absolute right-2.5 top-1/2 -translate-y-1/2 w-7 h-7 grid place-items-center rounded-full text-text-3 hover:text-text">
            <X size={14} />
          </button>
        )}
    </div>
  )
}

const Kosong = ({ children }: { children: ReactNode }) => (
  <div className="text-[13.5px] text-text-3 py-10 text-center leading-relaxed rm-pudar">{children}</div>
)

/** Satu baris lagu — seluruh baris adalah tombol (satu ketukan). */
function BarisLagu({ i, nomor, judul, artis, gambar, lebar, karaoke, catatan, durasi, sibuk, mencari, onKlik }: {
  i: number; nomor?: number; judul: string; artis: string; gambar?: string; lebar?: boolean
  karaoke?: boolean; catatan?: string; durasi?: number
  sibuk: boolean; mencari?: boolean; onKlik: () => void
}) {
  const { t } = useBahasa()
  const tiga = nomor !== undefined && nomor <= 3
  return (
    <button onClick={onKlik} disabled={sibuk}
      style={{ ['--i' as string]: Math.min(i, 14) }}
      className={`${i < 14 ? 'rm-muncul ' : ''}rm-baris rm-tekan group w-full text-left rounded-2xl border px-3 py-2.5 flex items-center gap-3
        ${sibuk ? 'border-gold/40 bg-gold/[0.07]' : 'border-white/[0.07] bg-white/[0.035] hover:bg-white/[0.07] hover:border-white/15'}`}>
      {nomor !== undefined && (
        <span className={`w-7 text-center text-[15px] font-semibold tabular-nums shrink-0 ${tiga ? 'text-gold' : 'text-text-3'}`}>
          {nomor}
        </span>
      )}
      <Sampul judul={judul} artis={artis} gambar={gambar || undefined} warna={WARNA_KARTU[i % WARNA_KARTU.length]}
        className={`${lebar ? 'w-[68px]' : 'w-12'} h-12 rounded-xl text-[13px] shadow-[0_6px_16px_-8px_rgba(0,0,0,.9)]`} />
      <span className="min-w-0 flex-1">
        {/* 30 Okt: judul boleh 2 baris — judul YouTube panjang tidak terpotong jadi "…". */}
        <span className="block text-[16.5px] font-medium leading-snug line-clamp-2 break-words">{judul}</span>
        <span className="block text-[14.5px] text-text-3 truncate">
          {mencari
            ? <span className="text-gold-1">{t('karaoke.mencari')}</span>
            : <>
                {karaoke && <span className="inline-flex items-center gap-0.5 mr-1.5 text-[12.5px] font-semibold uppercase tracking-wide text-gold-1/90"><Mic2 size={12} /> {t('jelajah.lencanaKaraoke')}</span>}
                {artis}{durasi ? ` · ${jam(durasi)}` : ''}{catatan ? ` · ${catatan}` : ''}
              </>}
        </span>
      </span>
      <span className={`w-9 h-9 rounded-full grid place-items-center shrink-0 transition
        ${sibuk ? 'bg-gold/20 text-gold' : 'bg-white/[0.06] text-text-2 group-hover:bg-gold group-hover:text-black'}`}>
        {sibuk ? <Loader2 size={16} className="animate-spin" /> : <Plus size={17} />}
      </span>
    </button>
  )
}

// Dipakai Remote.tsx untuk toast "berhasil".
export const IkonBerhasil = () => <Check size={15} />
